import { useEffect, useMemo, useRef, useState } from 'react';

import { BetTooltip } from 'shared/components/ui/bet-tooltip/bet-tooltip';
import { BET_LEGEND_ITEMS } from 'shared/components/ui/bet-tooltip/bet-tooltip.constants';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { coinIcons } from 'shared/constants';
import { NULL_ADDRESS } from 'shared/constants/constants';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useRoomStore } from 'shared/store/roomStore';
import { genericFormatBet } from 'shared/utils/betFormater';
import { getCoinIcon } from 'shared/utils/getCoinIcon';
import { isMobileDevice } from 'shared/utils/isMobileDevice';

import info from 'assets/png/info.png?format=webp;avif&as=picture';

import { getActiveBetClasses } from './getActiveBetClasses';

type CoinSymbol = keyof typeof coinIcons;

interface IActiveBetProps {
  amIWinner: boolean;
  isGameFinished: boolean;
}

export const ActiveBet = ({ amIWinner, isGameFinished }: IActiveBetProps) => {
  const [betText, setBetText] = useState('');
  const [rawBet, setRawBet] = useState<number | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const iconAndTooltipRef = useRef<HTMLDivElement>(null);

  const { playerB, bet } = useRoomStore();
  const { selectedCurrency } = useCurrenciesStore();
  const { wrapperClass, innerClass } = getActiveBetClasses(isGameFinished, amIWinner);

  const shouldShowInfo = useMemo(
    () => BET_LEGEND_ITEMS.some((item) => item.match && betText.includes(item.match)),
    [betText],
  );

  useEffect(() => {
    if (bet) {
      let raw = bet;
      if (playerB.address !== NULL_ADDRESS) {
        raw = bet * 2;
      }
      const formattedBet = genericFormatBet(raw.toString(), 'text-base');
      setBetText(String(formattedBet));
      setRawBet(raw);
    }
  }, [bet, selectedCurrency, playerB, amIWinner]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showTooltip &&
        iconAndTooltipRef.current &&
        !iconAndTooltipRef.current.contains(e.target as Node)
      ) {
        setShowTooltip(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTooltip]);

  return (
    <div
      className={`relative z-30 m-auto h-[20px] w-full max-w-[156px] rounded-md p-[1px] sm:h-auto sm:min-w-[11.625rem] sm:rounded-xl lg:max-w-[100%] ${wrapperClass}`}
    >
      <div
        className={`relative flex h-full items-center justify-center gap-1.5 rounded-md sm:rounded-xl sm:py-2.5 lg:gap-3 ${innerClass}`}
      >
        <span
          className="text-[14px] font-bold leading-none text-white sm:text-[24px]"
          dangerouslySetInnerHTML={{ __html: betText }}
        />
        {getCoinIcon(
          selectedCurrency?.symbol as CoinSymbol | undefined,
          'h-auto max-h-3.5 w-auto max-w-3.5 sm:max-h-6 sm:max-w-6',
        )}

        {shouldShowInfo && (
          <div className="absolute right-1 sm:top-1" ref={iconAndTooltipRef}>
            <div className="relative flex items-center">
              <OptimizedImage
                imageData={info}
                alt="info"
                className="h-4 w-4 cursor-pointer sm:h-5 sm:w-5"
                onMouseEnter={!isMobileDevice() ? () => setShowTooltip(true) : undefined}
                onMouseLeave={!isMobileDevice() ? () => setShowTooltip(false) : undefined}
                onClick={() => isMobileDevice() && setShowTooltip((prev) => !prev)}
              />
              <BetTooltip
                showTip={showTooltip && shouldShowInfo}
                rawBet={rawBet}
                containerClassName="absolute right-[-15px] top-[20px] mr-2 min-w-[11.625rem] rounded-xl border border-gray-border bg-border p-[2px] shadow-lg"
                className="flex flex-col gap-1.5 rounded-xl px-3 py-2.5"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
